import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, makeStyles, Typography, Checkbox } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import {
  Button,
  PhotoCover,
  SelectField,
  StandardDialog,
  TextField,
} from '@roc/ui';
import { formatDate } from '@roc/feature-utils';
import {
  GalleryPhoto,
  LoanInspectionSummary,
  LoanInspectionType,
} from '../types/galleryTypes';

const useStyles = makeStyles(theme => ({
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  photoItem: {
    position: 'relative',
    cursor: 'pointer',
    border: '2px solid',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  photoItemSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 0 8px rgba(57, 188, 208, 0.5)',
  },
  photoItemUnselected: {
    borderColor: '#e0e0e0',
  },
  checkbox: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '2px',
    zIndex: 1,
  },
  photoInfo: {
    padding: '4px 8px',
    fontSize: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  selectAllButton: {
    marginTop: '8px',
  },
  actionSection: {
    marginTop: '24px',
  },
  searchRow: {
    marginTop: '12px',
  },
}));

interface MoveGalleryPhotosDialogProps {
  photos: GalleryPhoto[];
}

const inspectionOptionLabel = (inspection: LoanInspectionSummary) => {
  if (inspection.inspectionType === LoanInspectionType.DRAWS) {
    const draw = inspection.drawNumber
      ? `Draw ${inspection.drawNumber}`
      : 'Draw';
    return `${draw} (Draw ID: ${inspection.externalId2})`;
  }
  return `Internal Valuation (Property ID: ${inspection.externalId2})`;
};

const inspectionOptionDescription = (inspection: LoanInspectionSummary) => {
  const parts = [
    `Created ${formatDate(inspection.createdDate, 'MM/dd/yyyy hh:mm aaa')}`,
    inspection.propertyAddress,
    inspection.locked ? `Locked (${inspection.lockedReason})` : null,
  ];
  return parts.filter(Boolean).join(' • ');
};

export const MoveGalleryPhotosDialog = observer(
  ({ photos }: MoveGalleryPhotosDialogProps) => {
    const classes = useStyles();
    const { inspectionStore } = useStore();
    const { galleryStore } = inspectionStore;
    const {
      movePhotosOpen,
      loanInspections,
      inspectionsSearched,
      isSearchingInspections,
      isCopyingPhotos,
    } = galleryStore;

    const [selectedPhotoUuids, setSelectedPhotoUuids] = useState<Set<string>>(
      new Set()
    );
    const [loanId, setLoanId] = useState('');
    const [selectedInspectionId, setSelectedInspectionId] = useState('');

    // The store clears its own move data on open; this only resets what the form holds.
    useEffect(() => {
      if (movePhotosOpen) {
        setSelectedPhotoUuids(new Set());
        setLoanId('');
        setSelectedInspectionId('');
      }
    }, [movePhotosOpen]);

    const inspectionOptions = useMemo(
      () =>
        loanInspections.map(inspection => ({
          label: inspectionOptionLabel(inspection),
          description: inspectionOptionDescription(inspection),
          value: inspection.inspectionId.toString(),
          disabled: inspection.locked,
        })),
      [loanInspections]
    );

    const handlePhotoToggle = (photoUuid: string) => {
      const newSelected = new Set(selectedPhotoUuids);
      if (newSelected.has(photoUuid)) {
        newSelected.delete(photoUuid);
      } else {
        newSelected.add(photoUuid);
      }
      setSelectedPhotoUuids(newSelected);
    };

    const isAllSelected =
      photos.length > 0 && selectedPhotoUuids.size === photos.length;

    const handleSelectAll = () => {
      setSelectedPhotoUuids(
        isAllSelected ? new Set() : new Set(photos.map(photo => photo.uuid))
      );
    };

    // The endpoint takes a numeric loan id, so anything else is rejected before the round trip.
    const isLoanIdValid = /^\d+$/.test(loanId.trim());

    const handleSearch = () => {
      if (!isLoanIdValid) {
        return;
      }
      setSelectedInspectionId('');
      galleryStore.searchInspectionsByLoanId(loanId);
    };

    const handleSubmit = () => {
      galleryStore.copyPhotosToInspection(
        Array.from(selectedPhotoUuids),
        parseInt(selectedInspectionId)
      );
    };

    const isBusy = isSearchingInspections || isCopyingPhotos;

    const dialogContent = (
      <Box>
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ marginBottom: '16px' }}
        >
          Select the photos you wish to move, then search a loan to pick the
          inspection report they should be copied into.
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleSelectAll}
          className={classes.selectAllButton}
          fullWidth
          disabled={photos.length === 0}
          testId="select-all-gallery-photos"
        >
          {isAllSelected ? 'DESELECT ALL PHOTOS' : 'SELECT ALL PHOTOS'}
        </Button>

        {photos.length === 0 ? (
          <Box pt={2}>
            <Typography variant="body2" style={{ opacity: 0.4 }}>
              This folder has no photos to move.
            </Typography>
          </Box>
        ) : (
          <div className={classes.photoGrid}>
            {photos.map(photo => {
              const isSelected = selectedPhotoUuids.has(photo.uuid);
              return (
                <div
                  key={photo.uuid}
                  className={`${classes.photoItem} ${
                    isSelected
                      ? classes.photoItemSelected
                      : classes.photoItemUnselected
                  }`}
                  onClick={() => handlePhotoToggle(photo.uuid)}
                >
                  <Checkbox
                    checked={isSelected}
                    className={classes.checkbox}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      handlePhotoToggle(photo.uuid);
                    }}
                  />
                  <PhotoCover
                    url={photo.thumbnailImage || photo.image}
                    rotationDegrees={photo.preferredRotation || 0}
                    width="100%"
                    height="120px"
                    caption={photo.caption}
                  />
                  <Box className={classes.photoInfo}>
                    <Typography variant="caption">
                      {formatDate(
                        photo.captureTimeIso,
                        'MM-dd-yyyy hh:mm:ss aaa'
                      )}
                    </Typography>
                  </Box>
                </div>
              );
            })}
          </div>
        )}

        <Box className={classes.actionSection}>
          <Typography variant="h6">
            Where do you want to copy these photos?
          </Typography>

          <Grid
            container
            spacing={2}
            alignItems="center"
            className={classes.searchRow}
          >
            <Grid item xs={12} sm={8}>
              <TextField
                label="Loan ID"
                testId="move-gallery-photos-loan-id"
                value={loanId}
                onChange={e => setLoanId(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter' && !isBusy) {
                    handleSearch();
                  }
                }}
                error={!!loanId.trim() && !isLoanIdValid}
                helperText={
                  !!loanId.trim() && !isLoanIdValid
                    ? 'Enter a numeric loan id'
                    : undefined
                }
                variant="outlined"
                fullWidth
                standaloneLabel
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={!isLoanIdValid || isBusy}
                fullWidth
                testId="search-loan-inspections"
              >
                {isSearchingInspections ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>

          {inspectionsSearched && loanInspections.length === 0 && (
            <Box pt={2}>
              <Typography variant="body2" color="error">
                No draw or internal valuation inspections found for this loan.
              </Typography>
            </Box>
          )}

          {loanInspections.length > 0 && (
            <Box pt={2}>
              <SelectField
                label="Select the inspection report for these photos"
                options={inspectionOptions}
                value={selectedInspectionId}
                onChange={value => setSelectedInspectionId(value)}
                fullWidth
                variant="outlined"
                standaloneLabel
                testId="select-destination-inspection"
                disabled={isBusy}
                // Keep the closed field on a single line; the created date stays in the menu.
                renderValue={(value: string) =>
                  inspectionOptions.find(option => option.value === value)
                    ?.label || ''
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    );

    const dialogActions = (
      <>
        <Button
          variant="outlined"
          onClick={() => galleryStore.closeMovePhotosDialog()}
          disabled={isCopyingPhotos}
          style={{ marginRight: '8px' }}
          testId="cancel-move-gallery-photos"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            isBusy || selectedPhotoUuids.size === 0 || !selectedInspectionId
          }
          testId="submit-move-gallery-photos"
        >
          {isCopyingPhotos ? 'Copying...' : 'Copy Photos'}
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={movePhotosOpen}
        handleClose={() => galleryStore.closeMovePhotosDialog()}
        title="Move Photos to Inspection Report"
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        maxWidth="sm"
        fullWidth
        removeContentBox
      />
    );
  }
);
